/**
 * @typedef {Object} VideoPlayerOptions
 * @description General options that specify behaviors for the VideoPlayer wrapper class
 *
 * @property {HTMLVideoElement} [video]
 * @property {Number} [positionUpdateMinDelta] Defaults to .9
 * @property {Number} [skipDebounceTime] Defaults to 500
 */

import {Emitter} from '../../../../shared/base/General';
import castWrapper from './CastFramework';

/**
 * @class VideoPlayer
 *
 * @property {VideoSource} currentSource
 * @property {HTMLVideoElement} video
 *
 * = State
 * @property {Boolean} loading
 * @property {Boolean} loaded
 * @property {Boolean} canPlay
 * @property {Boolean} playing
 * @property {Boolean} casting
 * @property {Boolean} castingEnabled
 *
 * @property {Number} duration
 * @property {Number} position
 * @property {Number} _position Actual current position, for the position prop is throttled
 * @property {Number[][]} buffered
 *
 * @property {Number} volume
 * @property {Boolean} muted
 *
 * @property {Number} skipAccumulator
 * @property {Number} skipTimeout
 * @property {Boolean} skipPlayOnSeek
 * 
 * = Settings
 * @property {Number} positionUpdateMinDelta
 * @property {Number} skipDebounceTime
 */
export class VideoPlayer extends Emitter {

	static Events = {
		source: 'source',
		sourceSwap: 'sourceSwap',
		play: 'play',
		pause: 'pause',
		end: 'end',
		seek: 'seek',
		mute: 'mute',
		unmute: 'unmute',

		updateVolume: 'update:volume',
		updateMuted: 'update:muted',
		updateDuration: 'update:duration',
		updatePosition: 'update:position',
		updateCanPlay: 'update:canPlay'
	};

	/**
	 * @param {VideoPlayerOptions} [options]
	 */
	constructor(options = {}) {
		super();
		this.setInitialState(options);

		this._bindVideoEvents();
		this._bindCastEvents();
	}

	/**
	 * @param {VideoPlayerOptions} [options]
	 */
	setInitialState(options) {
		Object.assign(this, {
			currentSource: null,

			video: null,

			// State
			loaded: false,
			loading: false,
			canPlay: false,
			playing: false,
			casting: false,
			castingEnabled: false,

			// Data
			duration: 0,
			position: 0,
			_position: 0,
			buffered: [],

			volume: 1,
			muted: false,

			skipAccumulator: null,
			skipTimeout: null,
			skipPlayOnSeek: null,

			positionUpdateMinDelta: .9,
			skipDebounceTime: 500,

			debug: true
		}, options);
	}

	/**
	 * Sets the video element this instance will be bound to
	 * @param {HTMLVideoElement} el
	 */
	setVideoElement(el) {
		this.video = el;
		this._bindVideoEvents();
	}

	reset() {
		Object.assign(this, {
			loaded: false,
			loading: false,
			canPlay: false,
			playing: false,

			// Data
			duration: 0,
			position: 0,
			_position: 0,
			buffered: []
		});
	}

	/// Actions

	// Source
	setSource(source) {
		this.currentSource = source;

		this.video.src = source ? source.url : '';

		if (this.casting) {
			this.castCurrentMedia();
		}

		this.emit('source', this.currentSource);
	}
	swapSource(source) {
		const currentPosition = this._position;
		const playing = this.playing;

		this.setSource(source);
		this.pause();

		this.seek(currentPosition);
		if (playing) this.play();

		this.emit('sourceSwap', this.currentSource);
	}

	// Media state
	play() {
		if (!this.casting) {
			this.video.play();
			this.playing = true;
		} else {
			castWrapper.play();
		}

		this.emit('play');
	}
	pause() {
		if (!this.casting) {
			this.video.pause();
			this.playing = false;
		} else {
			castWrapper.pause();
		}

		this.emit('pause');
	}
	togglePlay() {
		if (this.playing) {
			this.pause();
		} else {
			this.play();
		}
	}
	stop() {
		this.video.pause();
		this.playing = false;
		this.emit('end')
	}

	// Media Position
	seek(position) {
		if (!this.casting) {
			this.video.currentTime = position;
		} else {
			castWrapper.seek(position);
		}

		this.emit('seek', position);
	}
	skip(seconds) {
		if (this.skipPlayOnSeek === null) {
			this.skipPlayOnSeek = this.playing;

			if (this.playing) {
				this.pause();
			}
		}
		if (this.skipTimeout) clearTimeout(this.skipTimeout);

		this.position += seconds;
		this.skipAccumulator += seconds;
		if (this.position < 0) {
			this.position = 0;
		}
		if (this.position > this.duration) {
			this.position = this.duration;
		}

		this.skipTimeout = setTimeout(() => this._skip(), this.skipDebounceTime)
	}
	_skip() {
		this.seek(this._position + this.skipAccumulator);
		this.updatePosition();
		if (this.skipPlayOnSeek) {
			setTimeout(() => this.play(),0);
		}

		this.skipAccumulator = 0;
		this.skipPlayOnSeek = null;
		this.skipTimeout = null;
	}

	// Volume
	setVolume(value) {
		if (value < 0) value = 0;
		if (value > 1) value = 1;

		this.video.volume = value;
		this.volume = value;

		this.emit('update:volume', this.volume);

		if (value && this.muted) {
			this.unmute();
		} else if (!value && !this.muted) {
			this.mute();
		}
	}
	mute() {
		this.video.muted = true;
		this.muted = true;

		this.emit('update:muted', this.muted);
		this.emit('mute');
	}
	unmute() {
		this.video.muted = false;
		this.muted = false;

		this.emit('update:muted', this.muted);
		this.emit('unmute');
	}
	toggleMute() {
		if (this.muted) {
			this.unmute();
		} else {
			this.mute();
		}
	}

	// Casting
	async toggleCast() {
		if (!castWrapper.session) {
			await castWrapper.requestSession();
			this.casting = true;
			this.castCurrentMedia();
		} else {
			castWrapper.endSession();
			this.casting = false;
		}
	}

	async castCurrentMedia() {
		if (this.currentSource) {
			await castWrapper.loadMedia(this.currentSource.url);
		}
	}
	
	
	/// Sync Methods
	updateDuration() {
		this.duration = this.video.duration;
		this.emit('update:duration');
	}
	
	updatePosition() {
		if (!this.casting) {
			this._position = this.video.currentTime;
		} else {
			this._position = castWrapper.player.currentTime;
		}

		if (Math.abs(this._position - this.position) >= this.positionUpdateMinDelta) {
			this.position = this._position;
			this.emit('update:position', this.position);
		}
	}
	
	updateVolume() {
		if (this.volume !== this.video.volume) {
			this.volume = this.video.volume;

			this.emit('update:volume', this.volume);
		}

		if (this.muted !== this.video.muted) {
			this.muted = this.video.muted;

			this.emit('update:muted', this.muted);
		}
	}
	
	updateLoadProgress() {
		this.buffered = [];
		for (let i = 0; i < this.video.buffered.length; i++) {
			this.buffered.push([
				this.video.buffered.start(i),
				this.video.buffered.end(i)
			])
		}
	}

	setCanPlay(value) {
		this.canPlay = !!value;
		this.loading = !value;

		this.emit('update:canPlay', this.canPlay);
	}

	
	/// Events
	_bindCastEvents() {
		castWrapper.on('connect', () => this.casting = true);
		castWrapper.on('disconnect', () => this.casting = false);

		castWrapper.on('available', () => this.castingEnabled = true);
		castWrapper.on('unavailable', () => this.castingEnabled = false);

		castWrapper.on('play', (...args) => this.onPlay(...args));
		castWrapper.on('pause', (...args) => this.onPause(...args));
		castWrapper.on('waiting', (...args) => this.onWaiting(...args));
		castWrapper.on('stalled', (...args) => this.onStalled(...args));
		castWrapper.on('timeupdate', (...args) => this.onTimeUpdate(...args));
	}

	_bindVideoEvents() {
		if (!this.video) return;

		this.video.addEventListener('abort', (...args) => this.onError(...args));
		this.video.addEventListener('error', (...args) => this.onError(...args));

		this.video.addEventListener('loadstart', (...args) => this.onLoadStart(...args));
		this.video.addEventListener('loadedmetadata', (...args) => this.onLoadedMetadata(...args));
		this.video.addEventListener('loadeddata', (...args) => this.onLoadedData(...args));
		this.video.addEventListener('progress', (...args) => this.onProgress(...args));
		this.video.addEventListener('canplay', (...args) => this.onCanPlay(...args));
		this.video.addEventListener('canplaythrough', (...args) => this.onCanPlayThrough(...args));
		this.video.addEventListener('playing', (...args) => this.onPlaying(...args));
		this.video.addEventListener('waiting', (...args) => this.onWaiting(...args));
		this.video.addEventListener('stalled', (...args) => this.onStalled(...args));
		this.video.addEventListener('emptied', (...args) => this.onEmptied(...args));

		this.video.addEventListener('play', (...args) => this.onPlay(...args));
		this.video.addEventListener('pause', (...args) => this.onPause(...args));
		this.video.addEventListener('seeking', (...args) => this.onSeeking(...args));
		this.video.addEventListener('seeked', (...args) => this.onSeeked(...args));
		this.video.addEventListener('ended', (...args) => this.onEnded(...args));

		this.video.addEventListener('timeupdate', (...args) => this.onTimeUpdate(...args));
		this.video.addEventListener('volumechange', (...args) => this.onVolumeChange(...args));
		this.video.addEventListener('durationchange', (...args) => this.onDurationChange(...args))
	}

	onError(e) {
		// console.error('Better error handling is still pending');
		console.error(e);
		this.reset();
	}

	onLoadStart() {
		// Fired when the browser has started to load a resource.
		this.loading = true;
	}
	onLoadedMetadata() {
		// Fired when the metadata has been loaded
	}
	onLoadedData() {
		// Fired when the first frame of the media has finished loading.
		this.loading = false;
		this.loaded = true;
		this.updateLoadProgress();
	}
	onProgress() {
		// Fired periodically as the browser loads a resource.
		this.updateLoadProgress();
	}
	onCanPlay() {
		// Fired when the user agent can play the media, but estimates that not enough data has been loaded
		// to play the media up to its end without having to stop for further buffering of content.
		this.setCanPlay(true);

		if (this.debug) console.log('[VideoPlayer] native.canPlay');
	}
	onCanPlayThrough() {
		// Fired when the user agent can play the media, and estimates that enough data has been loaded to
		// play the media up to its end without having to stop for further buffering of content.
		this.setCanPlay(true);
		this.updateLoadProgress();

		if (this.debug) console.log('[VideoPlayer] native.canPlay');
	}
	onPlaying() {
		// Fired when playback is ready to start after having been paused or delayed due to lack of data
		this.setCanPlay(true);
		this.updateLoadProgress();

		if (this.debug) console.log('[VideoPlayer] native.playing');
	}
	onWaiting() {
		// Fired when playback has stopped because of a temporary lack of data
		this.setCanPlay(false);

		if (this.debug) console.log('[VideoPlayer] native.waiting');
	}
	onStalled() {
		// Fired when the user agent is trying to fetch media data, but data is unexpectedly not forthcoming.
		this.setCanPlay(false);

		if (this.debug) console.log('[VideoPlayer] native.stalled');
	}
	onEmptied() {
		// Fired when the user agent is trying to fetch media data, but data is unexpectedly not forthcoming.
		this.setCanPlay(false);
		this.updateLoadProgress();

		if (this.debug) console.log('[VideoPlayer] native.emptied');
	}

	// Media State
	onPlay() {
		// Fired when when the paused property is changed from true to false, as a result of the
		// HTMLMediaElement.play() method, or the autoplay attribute
		this.playing = true;
		this.setCanPlay(true);

		if (this.debug) console.log('[VideoPlayer] native.play');
	}
	onPause() {
		// Fired when a request to pause play is handled and the activity has entered its paused state,
		// most commonly occurring when the media's HTMLMediaElement.pause() method is called.
		this.playing = false;

		if (this.debug) console.log('[VideoPlayer] native.pause');
	}
	onSeeking() {
		// Fired when a seek operation begins

		if (this.debug) console.log('[VideoPlayer] native.seeking');
	}
	onSeeked() {
		// Fired when a seek operation completes

		if (this.debug) console.log('[VideoPlayer] native.seeked');
	}
	onEnded() {
		// Fired when playback stops when end of the media (<audio> or <video>) is reached or because
		// no further data is available.
		this.stop();

		if (this.debug) console.log('[VideoPlayer] native.ended');
	}

	// Media meta data
	onTimeUpdate() {
		// Fired when the time indicated by the currentTime attribute has been updated
		this.updatePosition();
		this.setCanPlay(true);
	}
	onVolumeChange() {
		// Fired when the volume has changed.
		this.updateVolume();
	}
	onDurationChange() {
		// Fired when the duration attribute has been updated.
		this.updateDuration();
	}
}


export class VideoCue {
	constructor(label, position, visible = true) {
		this.label = label;
		this.position = position;
		this.visible = visible;
	}
}